import { useCallback, useMemo } from "react";
import { useScopedInfiniteQuery } from "@/hooks/use-scoped-infinite-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import {
  calendarConfigQuery,
  configOfPages,
  holidayCalendarDatesKey,
  holidayCalendarDatesQuery,
  type HolidayDatesFilter,
  holidayCalendarPeopleQuery,
  holidayCalendarQuery,
  holidayCalendarsQuery,
  holidayCalendarSuggestionsQuery,
  holidayRegionsQuery,
  myCalendarSourcesQuery,
  myHolidayCalendarQuery,
} from "@/queries/calendars";
import { GLOBAL_TENANT } from "@/queries/scope";
import { useStore } from "@/stores/store-context";
import type {
  ICalendarSource,
  IGoogleDomainWideConfig,
  IHolidayCalendar,
  IHolidayCalendarPerson,
  IHolidayCalendarSuggestion,
  IHolidayCountry,
  IOutlookDomainWideConfig,
} from "@/types/workspace";

/*
 * The calendar readers (`app/queries/calendars.ts`, NG-2814). Each observes
 * its cached entry, so a calendar write in this tab shows on every screen that
 * reads it. All must be called from an `observer` component.
 */

const NO_SOURCES: ICalendarSource[] = [];
const NO_COUNTRIES: IHolidayCountry[] = [];
const NO_CALENDARS: IHolidayCalendar[] = [];
const NO_SUGGESTIONS: IHolidayCalendarSuggestion[] = [];
const NO_PEOPLE: IHolidayCalendarPerson[] = [];

/** The signed-in user's own calendar connections, read while `enabled`. */
export function useMyCalendarSources({ enabled = true }: { enabled?: boolean } = {}) {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedQuery(
    GLOBAL_TENANT,
    (scope) => myCalendarSourcesQuery(workspaceCalendarService, scope),
    { enabled },
  );
  return query.data ?? NO_SOURCES;
}

/**
 * The country and region catalogue the pickers offer. Optional picker data: a
 * failed read (logged by the cache) leaves the picker with no options.
 */
export function useHolidayCountries(): IHolidayCountry[] {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedQuery(GLOBAL_TENANT, (scope) =>
    holidayRegionsQuery(workspaceCalendarService, scope),
  );
  return query.data ?? NO_COUNTRIES;
}

/**
 * The user's own public-holiday region in the workspace (`null` when unset), and
 * whether the first read has answered. A form seeded from it waits for `settled`,
 * so it never seeds from "not loaded yet" and then gets overwritten.
 */
export function useMyHolidayCalendar(workspaceSlug: string | undefined): {
  calendar: ICalendarSource | null;
  settled: boolean;
} {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedQuery(
    workspaceSlug,
    (scope) => myHolidayCalendarQuery(workspaceCalendarService, scope),
    { enabled: Boolean(workspaceSlug) },
  );
  return { calendar: query.data ?? null, settled: query.isSuccess || query.isError };
}

/** A workspace's holiday calendars, with whether the first read is still out. */
export function useHolidayCalendars(workspaceSlug: string | undefined): {
  calendars: IHolidayCalendar[];
  isLoading: boolean;
} {
  const { workspaceCalendarService } = useStore();
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => holidayCalendarsQuery(workspaceCalendarService, scope),
    { enabled: Boolean(workspaceSlug) },
  );
  return {
    calendars: query.data ?? NO_CALENDARS,
    isLoading: Boolean(workspaceSlug) && (mode === "waiting" || query.isPending),
  };
}

/** Groups of people no holiday calendar covers; empty until read, or on a failed read. */
export function useHolidayCalendarSuggestions(
  workspaceSlug: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): IHolidayCalendarSuggestion[] {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedQuery(
    workspaceSlug,
    (scope) => holidayCalendarSuggestionsQuery(workspaceCalendarService, scope),
    { enabled: enabled && Boolean(workspaceSlug) },
  );
  return query.data ?? NO_SUGGESTIONS;
}

/**
 * One holiday calendar, with whether it has answered (found or not). With
 * `enabled: false` it only observes what the cache already holds.
 */
export function useHolidayCalendar(
  workspaceSlug: string | undefined,
  calendarId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
): { calendar: IHolidayCalendar | undefined; settled: boolean } {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedQuery(
    workspaceSlug,
    (scope) => holidayCalendarQuery(workspaceCalendarService, scope, calendarId ?? ""),
    { enabled: enabled && Boolean(workspaceSlug && calendarId) },
  );
  return { calendar: query.data, settled: query.isSuccess || query.isError };
}

/**
 * Who one holiday calendar covers. `loaded` is a successful read: without it
 * an outage would read as "this calendar matches nobody" (NG-587).
 */
export function useHolidayCalendarPeople(
  workspaceSlug: string | undefined,
  calendarId: string | undefined,
): { people: IHolidayCalendarPerson[]; isLoading: boolean; loaded: boolean } {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedQuery(
    workspaceSlug,
    (scope) => holidayCalendarPeopleQuery(workspaceCalendarService, scope, calendarId ?? ""),
    { enabled: Boolean(workspaceSlug && calendarId) },
  );
  return {
    people: query.data?.results ?? NO_PEOPLE,
    isLoading: query.isFetching,
    loaded: query.data !== undefined,
  };
}

/** A provider's domain-wide config and its member pages, read while `enabled`. */
export function useCalendarConfig<T extends IGoogleDomainWideConfig | IOutlookDomainWideConfig>(
  workspaceSlug: string | undefined,
  provider: "google" | "outlook",
  { enabled = true }: { enabled?: boolean } = {},
): {
  config: T | null;
  hasMoreMembers: boolean;
  membersLoading: boolean;
  loadMoreMembers: () => Promise<void>;
} {
  const { workspaceCalendarService } = useStore();
  const { query } = useScopedInfiniteQuery(
    workspaceSlug,
    (scope) => calendarConfigQuery(workspaceCalendarService, scope, provider),
    { enabled: enabled && Boolean(workspaceSlug) },
  );
  const data = query.data;
  const config = useMemo(() => configOfPages<T>(data), [data]);
  const fetchNextPage = query.fetchNextPage;
  const loadMoreMembers = useCallback(async () => {
    await fetchNextPage();
  }, [fetchNextPage]);
  return {
    config,
    hasMoreMembers: query.hasNextPage,
    membersLoading: query.isFetching,
    loadMoreMembers,
  };
}

/**
 * One calendar's dates for `filter`, a page at a time, with the cache key the
 * panel's own row edits patch (`null` before the session resolves).
 */
export function useHolidayCalendarDates(
  workspaceSlug: string,
  calendarId: string,
  filter: HolidayDatesFilter,
) {
  const { workspaceCalendarService } = useStore();
  const { mode, query } = useScopedInfiniteQuery(
    workspaceSlug,
    (scope) => holidayCalendarDatesQuery(workspaceCalendarService, scope, calendarId, filter),
    { enabled: Boolean(workspaceSlug && calendarId) },
  );
  const scope = useQueryScope(mode === "cache" ? workspaceSlug : undefined);
  const key = scope ? holidayCalendarDatesKey(scope, calendarId, filter) : null;
  return { mode, query, key };
}
